// Agentur-Landing — state-of-the-art, animierte Sektionen, DOM-Mockups statt PNGs.
// Inspiriert von lead-table.com (Sticky-Feature-Tabs, Vergleichssektion).
// PV + Wärmepumpe + Kombi explizit adressiert. Wholesale-Preise.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sun, Thermometer, Zap, Flame, TrendingUp, Users, FileText, Send, Sparkles,
  Check, X, ArrowRight, ChevronDown, Wallet, ShieldCheck, Inbox, Mail,
  Building2, Briefcase, MessageSquare, PhoneCall, Star, Quote,
} from "lucide-react";
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, animate, useInView } from "framer-motion";
import { Button } from "@/components/ui/button";

// ============= ROOT =============
export default function AgencyLanding() {
  // Globalen reduced-motion-Hint respektieren
  return (
    <div className="min-h-screen bg-[#fafaf9] text-slate-900 selection:bg-orange-200" data-testid="agency-landing-v2" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
      <NavBar />
      <Hero />
      <LogoStrip />
      <Comparison />
      <FeatureShowcase />
      <MultiProduct />
      <WholesaleCalculator />
      <Pricing />
      <Testimonials />
      <HowItWorks />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}

// ============= NAV =============
function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);
  return (
    <header className={`sticky top-0 z-50 transition-all ${scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/60" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 grid place-items-center text-white shadow-md group-hover:rotate-12 transition-transform">
            <Sun className="w-5 h-5" />
          </div>
          <span className="text-lg font-extrabold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>revenioo<span className="text-orange-600">.ai</span></span>
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm text-slate-600">
          <a href="#features" className="hover:text-slate-900 transition-colors">Funktionen</a>
          <a href="#multi-produkt" className="hover:text-slate-900 transition-colors">PV & Wärmepumpe</a>
          <a href="#marge" className="hover:text-slate-900 transition-colors">Marge</a>
          <a href="#pricing" className="hover:text-slate-900 transition-colors">Preise</a>
          <a href="#faq" className="hover:text-slate-900 transition-colors">FAQ</a>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-slate-700 hover:text-slate-900 px-3 py-2">Login</Link>
          <Link to="/login" data-testid="nav-cta" className="text-sm font-bold bg-orange-600 hover:bg-orange-700 text-white px-4 py-2.5 rounded-lg shadow-[0_8px_24px_-8px_rgba(234,88,12,0.6)] hover:-translate-y-0.5 transition-all">Demo buchen</Link>
        </div>
      </div>
    </header>
  );
}

// ============= HERO =============
function Hero() {
  return (
    <section className="relative pt-12 md:pt-20 pb-20 md:pb-32 overflow-hidden">
      {/* Hintergrund-Effekte */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] bg-gradient-radial from-orange-200/40 via-transparent to-transparent blur-3xl" />
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-violet-200/30 rounded-full blur-3xl" />
        <NoiseOverlay />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.08 } } }} className="lg:col-span-6">
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm text-xs font-semibold text-slate-700 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            White-Label · Wholesale · für Agenturen
          </motion.div>
          <motion.h1 variants={fadeUp} className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter text-slate-900 leading-[0.95]" style={{ fontFamily: "Manrope, sans-serif" }}>
            Das <span className="relative inline-block">
              <span className="relative z-10">Betriebssystem</span>
              <span className="absolute inset-x-0 bottom-1 h-3 bg-orange-300/60 -z-0 rounded-sm" />
            </span><br />
            für <span className="text-orange-600">Energie-Agenturen</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl">
            Verwalten Sie Mandanten aus <strong className="text-slate-900">Photovoltaik</strong> und <strong className="text-slate-900">Wärmepumpe</strong> in einer Plattform.
            Eigenes Branding, Hot-Lead-Erkennung, Preisschätzungs-Engine und vorqualifizierte Leads — Wholesale-Konditionen ab 99 €/Mandant.
          </motion.p>
          <motion.div variants={fadeUp} className="mt-9 flex flex-wrap gap-3">
            <Link to="/login" data-testid="hero-cta-primary" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-7 py-4 rounded-xl font-bold text-base shadow-[0_0_40px_-10px_rgba(234,88,12,0.7)] hover:shadow-[0_0_60px_-12px_rgba(234,88,12,0.8)] hover:-translate-y-1 transition-all">
              Demo buchen <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#features" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 bg-white border-2 border-slate-200 hover:border-slate-300 text-slate-800 px-7 py-4 rounded-xl font-bold text-base transition-all hover:bg-slate-50">
              Funktionen ansehen
            </a>
          </motion.div>
          <motion.div variants={fadeUp} className="mt-8 flex items-center gap-6 text-sm text-slate-500">
            <div className="flex -space-x-2">
              {["#fdba74", "#a78bfa", "#22d3ee", "#34d399"].map((c, i) => (
                <div key={i} className="w-8 h-8 rounded-full border-2 border-white" style={{ background: c }} />
              ))}
            </div>
            <div>
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map((s) => <Star key={s} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
              </div>
              <p className="text-xs mt-0.5">DSGVO-konform · Server in der EU</p>
            </div>
          </motion.div>
        </motion.div>

        {/* Hero-Mockup: Agentur-Dashboard + floating elements */}
        <div className="lg:col-span-6 relative">
          <HeroMockup />
        </div>
      </div>
    </section>
  );
}

function HeroMockup() {
  // Floating-Bubble Daten
  const floats = [
    { Icon: Flame, text: "+1 Hot Lead", color: "from-orange-500 to-rose-500", style: "top-4 -left-6", delay: 0.5 },
    { Icon: Wallet, text: "650 € Marge", color: "from-emerald-500 to-teal-500", style: "bottom-8 -left-8", delay: 0.8 },
    { Icon: Sparkles, text: "Magic Link geklickt", color: "from-violet-500 to-fuchsia-500", style: "-top-4 right-8", delay: 1.1 },
  ];
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="relative">
      {/* Glow */}
      <div className="absolute -inset-8 bg-gradient-to-tr from-orange-400/30 via-violet-400/20 to-transparent blur-3xl -z-10 rounded-[3rem]" />

      <MockAgencyDashboard tilt />

      {/* Floating UI Bubbles */}
      {floats.map((f, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0.7, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: f.delay, duration: 0.5 }}
          className={`absolute ${f.style} hidden md:block`}
        >
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 3 + i * 0.5, repeat: Infinity, ease: "easeInOut" }}
            className={`bg-gradient-to-r ${f.color} text-white px-3 py-2 rounded-xl shadow-2xl flex items-center gap-2 backdrop-blur`}
          >
            <f.Icon className="w-4 h-4" />
            <span className="text-xs font-bold whitespace-nowrap">{f.text}</span>
          </motion.div>
        </motion.div>
      ))}
    </motion.div>
  );
}

// ============= LOGO STRIP =============
function LogoStrip() {
  const logos = ["SolarFox", "WärmeWelt", "EnergieGruppe", "PV-Direkt", "ÖkoTherm", "Heliosa"];
  return (
    <section className="py-12 border-y border-slate-200/70 bg-white/40">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <p className="text-center text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-6">Vertraut von Agenturen in DACH</p>
        <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4">
          {logos.map((l, i) => (
            <motion.div key={l} initial={{ opacity: 0 }} whileInView={{ opacity: 0.6 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="text-slate-400 font-bold text-xl tracking-tight hover:opacity-100 hover:text-slate-700 transition-all" style={{ fontFamily: "Manrope, sans-serif" }}>
              {l}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============= VERGLEICH =============
function Comparison() {
  const without = [
    "Excel-Tabellen, Zapier-Krücken, Tool-Wildwuchs",
    "Leads versanden im E-Mail-Postfach des Kunden",
    "Hot-Lead-Erkennung? Nur Gefühl, keine Daten",
    "Wärmepumpen-Anfragen mit anderem Tool getrennt vom PV-CRM",
    "Pro Mandant manuelle Whitelabel-Konfiguration",
    "Keine Margen-Transparenz — Kunde fragt, Sie raten",
  ];
  const withUs = [
    "Eine Plattform — Mandanten, Leads, Pipelines, Reports",
    "Conversion Queue priorisiert Hot-Leads automatisch",
    "Solar + WP + Kombi mit eigener Estimate-Engine pro Pfad",
    "White-Label-Mandant in 60 Sekunden onboarding-bereit",
    "Wholesale-Marge live: 799 € verkauft − 149,17 € eingekauft (Jahres-Buchung)",
    "DSGVO-Servers in Frankfurt, eigene Subdomain pro Kunde",
  ];
  return (
    <section className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <SectionHeader
          eyebrow="Warum revenioo.ai"
          title="Ihr Agentur-Alltag — vorher vs. nachher"
          sub="Sie holen mehr aus den gleichen Leads. Ihre Mandanten merken den Unterschied im ersten Monat."
        />
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mt-14">
          <CompareCol icon={X} title="Ohne revenioo.ai" items={without} negative />
          <CompareCol icon={Check} title="Mit revenioo.ai" items={withUs} />
        </div>
      </div>
    </section>
  );
}
function CompareCol({ icon: Icon, title, items, negative = false }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <div ref={ref} className={`p-7 md:p-9 rounded-2xl ${negative ? "bg-slate-100 border border-slate-200" : "bg-gradient-to-br from-slate-900 to-slate-800 text-white border border-slate-700 shadow-2xl"}`}>
      <div className="flex items-center gap-3 mb-7">
        <div className={`w-11 h-11 rounded-xl grid place-items-center ${negative ? "bg-slate-200 text-slate-500" : "bg-orange-500/20 text-orange-400"}`}>
          <Icon className="w-5 h-5" />
        </div>
        <h3 className="text-2xl font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>{title}</h3>
      </div>
      <ul className="space-y-3">
        {items.map((it, i) => (
          <motion.li
            key={i}
            initial={{ opacity: 0, x: -16 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.05 * i, duration: 0.4 }}
            className="flex items-start gap-3"
          >
            <span className={`mt-1 flex-shrink-0 w-5 h-5 rounded-full grid place-items-center ${negative ? "bg-rose-100 text-rose-600" : "bg-emerald-500/20 text-emerald-400"}`}>
              {negative ? <X className="w-3 h-3" /> : <Check className="w-3 h-3" />}
            </span>
            <span className={`text-sm md:text-[15px] ${negative ? "text-slate-700" : "text-slate-200"}`}>{it}</span>
          </motion.li>
        ))}
      </ul>
    </div>
  );
}

// ============= FEATURE SHOWCASE (Sticky-Tab-Pattern) =============
const FEATURES = [
  { key: "clients", label: "Mandantenverwaltung", icon: Building2, desc: "Alle Solar- und Heizungsbetriebe in einer Übersicht. Eigenes Logo, Farbe, Subdomain. White-Label out of the box.", render: () => <MockClients /> },
  { key: "hotleads", label: "Hot-Lead-Erkennung", icon: Flame, desc: "Conversion Queue priorisiert automatisch — Sofort-Leads stehen oben. Ihre Mandanten verlieren keinen Anfrager mehr in der E-Mail-Flut.", render: () => <MockConversionQueue /> },
  { key: "multiproduct", label: "Multi-Produkt-Konfigurator", icon: Zap, desc: "Ein Konfigurator, drei Pfade: Solar, Wärmepumpe, Kombi. Mit Preisschätzung und Förderspannen — unverbindlich, aber verkaufsfähig.", render: () => <MockConfigurator /> },
  { key: "pipeline", label: "Vertriebs-Pipeline", icon: Briefcase, desc: "Kanban-Board pro Mandant: Anfrage → Angebot → Nachfass → Auftrag. Drag-and-Drop, Pipeline-Wert immer im Blick.", render: () => <MockPipeline /> },
  { key: "ai", label: "KI-Automation", icon: Sparkles, desc: "Reaktivierungs-Texte, Einwand-Coach, Follow-ups: KI hilft Ihren Mandanten, mehr Pipeline in Umsatz zu drehen.", render: () => <MockAIChat /> },
  { key: "reporting", label: "Reporting & Recovery", icon: TrendingUp, desc: "Geretteter Umsatz, reaktivierte Pipeline, Antwortquote — und alles aufgeschlüsselt nach Solar, WP und Kombi.", render: () => <MockReporting /> },
];

function FeatureShowcase() {
  const [active, setActive] = useState(FEATURES[0].key);
  const current = FEATURES.find((f) => f.key === active) || FEATURES[0];
  return (
    <section id="features" className="py-24 md:py-32 bg-white border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <SectionHeader
          eyebrow="Alles in einer Plattform"
          title="Sechs Funktionen, ein Workflow"
          sub="Sticky-Vorschau rechts — Tab links anklicken zum Wechseln."
        />
        <div className="mt-14 grid lg:grid-cols-12 gap-10">
          {/* Tab-Liste */}
          <div className="lg:col-span-5 space-y-2">
            {FEATURES.map((f) => {
              const isActive = f.key === active;
              return (
                <button
                  key={f.key}
                  onClick={() => setActive(f.key)}
                  data-testid={`feature-tab-${f.key}`}
                  className={`w-full text-left p-5 rounded-xl border-l-4 transition-all ${
                    isActive
                      ? "border-orange-500 bg-orange-50/60 shadow-sm"
                      : "border-transparent hover:border-slate-200 hover:bg-slate-50 opacity-70 hover:opacity-100"
                  }`}
                >
                  <div className="flex items-center gap-3 mb-1.5">
                    <f.icon className={`w-5 h-5 ${isActive ? "text-orange-600" : "text-slate-400"}`} />
                    <span className={`font-bold text-base ${isActive ? "text-slate-900" : "text-slate-700"}`} style={{ fontFamily: "Manrope, sans-serif" }}>{f.label}</span>
                  </div>
                  <AnimatePresence>
                    {isActive && (
                      <motion.p
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="text-sm text-slate-600 leading-relaxed overflow-hidden"
                      >
                        {f.desc}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </button>
              );
            })}
          </div>
          {/* Sticky Vorschau */}
          <div className="lg:col-span-7">
            <div className="lg:sticky lg:top-24">
              <div className="relative rounded-2xl bg-gradient-to-br from-slate-50 to-white p-3 border border-slate-200 shadow-2xl shadow-slate-300/40">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={active}
                    initial={{ opacity: 0, scale: 0.97, y: 8 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.97, y: -8 }}
                    transition={{ duration: 0.35 }}
                  >
                    {current.render()}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============= MULTI PRODUKT (USP gegen reine Solar/CRM-Tools) =============
function MultiProduct() {
  const items = [
    { Icon: Sun, color: "from-amber-400 to-orange-500", title: "Photovoltaik", lead: "12 aktive Leads", desc: "Klassischer PV-Funnel — vom Anfrageformular zur Preisschätzung mit Förderbeträgen." },
    { Icon: Thermometer, color: "from-sky-500 to-blue-600", title: "Wärmepumpe", lead: "BAFA-Förderung integriert", desc: "Eigener WP-Pfad mit Heizungstyp, Wohnfläche, Aufstellort — Schätzung inkl. BAFA-Spanne." },
    { Icon: Zap, color: "from-violet-500 to-fuchsia-600", title: "PV + Wärmepumpe", lead: "Höchster Auftragswert", desc: "Kombi-Pfad mit Breakdown pro Komponente und Gesamt-Estimate für den maximalen Hebel." },
  ];
  return (
    <section id="multi-produkt" className="py-24 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/2 left-1/4 w-[500px] h-[500px] bg-orange-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-violet-200/20 rounded-full blur-3xl" />
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <SectionHeader
          eyebrow="Nicht nur PV. Auch Wärmepumpe."
          title="Drei Produktpfade, ein Konfigurator"
          sub="Während reine Solar-CRMs ihre Kunden zwingen, das WP-Geschäft anders zu tracken, decken wir beide Welten ab — inklusive Förder-Logik pro Pfad."
        />
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {items.map((it, i) => (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              whileHover={{ y: -6 }}
              className="relative p-7 rounded-2xl bg-white border border-slate-200 shadow-lg hover:shadow-2xl transition-shadow group overflow-hidden"
            >
              <div className={`absolute -top-12 -right-12 w-40 h-40 bg-gradient-to-br ${it.color} opacity-10 rounded-full blur-2xl group-hover:opacity-20 transition-opacity`} />
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${it.color} grid place-items-center text-white shadow-lg mb-5`}>
                <it.Icon className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: "Manrope, sans-serif" }}>{it.title}</h3>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">{it.lead}</p>
              <p className="text-sm text-slate-600 leading-relaxed">{it.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Reporting-Mockup das alle 3 Pfade zeigt */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-12 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-200 p-5 shadow-xl"
        >
          <MockProductReporting />
        </motion.div>
      </div>
    </section>
  );
}

// ============= WHOLESALE-MARGE-CALCULATOR =============
function WholesaleCalculator() {
  const [count, setCount] = useState(10);
  // Beispielrechnung basiert auf Wholesale Growth JAHRESBUCHUNG:
  // 1.790 € / 12 = 149,17 € effektiver Monatsbetrag (10 zahlen, 12 nutzen).
  const WHOLESALE_PER_MONTH_YEARLY = 1790 / 12; // = 149.1666…
  const SELL_PRICE = 799;
  const revenue = Math.round(count * SELL_PRICE);
  const cost = Math.round(count * WHOLESALE_PER_MONTH_YEARLY);
  const profit = revenue - cost;
  const costPerClient = WHOLESALE_PER_MONTH_YEARLY.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <section id="marge" className="py-24 md:py-32 bg-gradient-to-br from-slate-900 to-slate-800 text-white relative overflow-hidden">
      <NoiseOverlay />
      <div className="absolute inset-0 -z-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-orange-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-violet-500/15 rounded-full blur-3xl" />
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
        <SectionHeader
          dark
          eyebrow="Wholesale-Marge · Jahresbuchung"
          title="Sie kaufen für 149,17 €, verkaufen für 799 €."
          sub="Effektiver Monatsbetrag bei Jahresbuchung von Wholesale Growth (1.790 € / 12). Pro Mandant. Jeden Monat. Skalierbar mit jedem weiteren Kunden."
        />

        <div className="mt-14 grid lg:grid-cols-5 gap-8 items-center">
          {/* Slider links */}
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-orange-300 font-bold mb-3">Mandanten-Slider</p>
            <div className="flex items-baseline gap-3 mb-6">
              <Counter value={count} className="text-7xl font-extrabold tracking-tighter text-white" style={{ fontFamily: "Manrope, sans-serif" }} />
              <span className="text-slate-400 text-lg">aktive Mandanten</span>
            </div>
            <input
              type="range"
              min="1"
              max="50"
              value={count}
              onChange={(e) => setCount(parseInt(e.target.value))}
              data-testid="margin-slider"
              className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-orange-500"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-2">
              <span>1</span><span>25</span><span>50</span>
            </div>
            <p className="text-xs text-slate-400 mt-6 leading-relaxed">
              Annahme: Wholesale Growth jährlich (1.790 €/Jahr = 149,17 €/Monat effektiv), 250 Leads/Monat.
              Ihr Verkaufspreis 799 € ist beispielhaft — Sie definieren ihn selbst.
            </p>
          </div>

          {/* Rechnung rechts */}
          <div className="lg:col-span-3 grid grid-cols-3 gap-3">
            <MoneyTile label="Ihr Umsatz" value={revenue} accent="text-white" sub={`${count} × 799 €`} />
            <MoneyTile label="Wholesale-Kosten" value={cost} accent="text-slate-400" sub={`${count} × ${costPerClient} €`} prefix="−" />
            <MoneyTile label="Ihr Rohertrag" value={profit} accent="text-orange-400" sub="monatlich" highlight />
          </div>
        </div>

        <div className="mt-12 max-w-2xl mx-auto text-center">
          <p className="text-slate-300 text-sm">
            Bei <strong className="text-white">{count} Mandanten</strong> verdienen Sie monatlich
            <strong className="text-orange-400 mx-1.5">{profit.toLocaleString("de-DE")} €</strong>
            Rohertrag — pro Jahr <strong className="text-orange-400">{(profit * 12).toLocaleString("de-DE")} €</strong>.
          </p>
        </div>
      </div>
    </section>
  );
}

function MoneyTile({ label, value, accent, sub, prefix = "", highlight = false }) {
  return (
    <div className={`p-5 rounded-2xl ${highlight ? "bg-gradient-to-br from-orange-500/20 to-orange-600/10 border border-orange-500/40" : "bg-white/5 border border-white/10"} backdrop-blur`}>
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 mb-3">{label}</p>
      <div className="flex items-baseline gap-1">
        {prefix && <span className={`text-2xl font-bold ${accent}`}>{prefix}</span>}
        <Counter value={value} className={`text-3xl md:text-4xl font-extrabold tracking-tighter tabular-nums ${accent}`} style={{ fontFamily: "Manrope, sans-serif" }} />
        <span className={`text-lg ${accent}`}>€</span>
      </div>
      <p className="text-xs text-slate-500 mt-1.5">{sub}</p>
    </div>
  );
}

// ============= PRICING =============
const PLANS = [
  { name: "Wholesale Starter", monthly: 99, yearly: 990, leads: "50", desc: "Für kleinere Betriebe & Testkunden." },
  { name: "Wholesale Growth", monthly: 179, yearly: 1790, leads: "250", desc: "Der Standard — passt für 90 % Ihrer Mandanten.", popular: true },
  { name: "Wholesale Scale", monthly: 299, yearly: 2990, leads: "1.000", desc: "Für Betriebe mit hohem Leadvolumen." },
];

function Pricing() {
  const [yearly, setYearly] = useState(false);
  return (
    <section id="pricing" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <SectionHeader
          eyebrow="Wholesale-Konditionen"
          title="Eine faire Preisstruktur, die mitwächst"
          sub="Drei Pakete · pro Mandant abgerechnet · Jahreszahlung: 2 Monate geschenkt"
        />
        <div className="mt-10 flex items-center justify-center gap-3" data-testid="pricing-toggle">
          <button
            data-testid="agency-pricing-toggle-monthly"
            onClick={() => setYearly(false)}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${!yearly ? "bg-slate-900 text-white shadow-md" : "text-slate-500 hover:text-slate-700"}`}
          >
            Monatlich
          </button>
          <button
            data-testid="agency-pricing-toggle-yearly"
            onClick={() => setYearly(true)}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all inline-flex items-center gap-2 ${yearly ? "bg-slate-900 text-white shadow-md" : "text-slate-500 hover:text-slate-700"}`}
          >
            Jährlich
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold" data-testid="agency-pricing-yearly-savings-badge">
              2 Monate geschenkt
            </span>
          </button>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {PLANS.map((p, i) => {
            const eff = yearly ? p.yearly / 12 : p.monthly;
            const effStr = yearly ? eff.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : eff.toLocaleString("de-DE");
            const slug = p.name.toLowerCase().replace(/\s/g, "-");
            return (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                whileHover={{ y: -8 }}
                data-testid={`plan-${slug}`}
                className={`relative p-7 rounded-2xl bg-white border ${p.popular ? "border-orange-500 shadow-2xl shadow-orange-500/20 ring-2 ring-orange-500/20" : "border-slate-200 shadow-lg"}`}
              >
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-600 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-md">Beliebt</span>
                )}
                <h3 className="font-bold text-slate-900 text-lg" style={{ fontFamily: "Manrope, sans-serif" }}>{p.name}</h3>
                <p className="text-sm text-slate-500 mt-1 min-h-[40px]">{p.desc}</p>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="text-5xl font-extrabold text-slate-900 tracking-tighter tabular-nums" style={{ fontFamily: "Manrope, sans-serif" }} data-testid={`agency-plan-amount-${slug}`}>{effStr}</span>
                  <span className="text-slate-500 text-base ml-1">€ / Monat</span>
                </div>
                <p className="text-xs text-slate-500 mt-2 min-h-[16px]" data-testid={`agency-plan-billing-note-${slug}`}>
                  {yearly ? `${p.yearly.toLocaleString("de-DE")} € pro Jahr · 2 Monate geschenkt` : "monatliche Abrechnung"}
                </p>
                <ul className="mt-6 space-y-2.5 text-sm text-slate-700">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600 flex-shrink-0" /> {p.leads} verarbeitete Leads / Monat</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600 flex-shrink-0" /> Alle Funktionen ohne Limits</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600 flex-shrink-0" /> Solar, Wärmepumpe & Kombi</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600 flex-shrink-0" /> White-Label-Branding</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600 flex-shrink-0" /> Hot-Lead-Recovery & KI-Chat</li>
                </ul>
                <Link to="/login" data-testid={`plan-cta-${slug}`} className={`mt-7 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold text-sm transition-all ${p.popular ? "bg-orange-600 hover:bg-orange-700 text-white shadow-md hover:shadow-lg" : "bg-slate-900 hover:bg-slate-800 text-white"}`}>
                  Jetzt starten <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Wholesale-Hinweis statt redundanter Beispielrechnung — die ausführliche
            Marge-Kalkulation steht bereits oben im interaktiven Slider. */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 max-w-2xl mx-auto text-center"
        >
          <p className="text-sm text-slate-500">
            Wholesale-Konditionen exklusiv für Agentur-Partner ·
            <a href="#marge" className="text-orange-600 hover:text-orange-700 font-semibold ml-1">Marge berechnen</a>
          </p>
        </motion.div>
      </div>
    </section>
  );
}

// ============= TESTIMONIALS =============
function Testimonials() {
  const t = [
    { name: "Sonja Berg", role: "Geschäftsführerin · SonnenWelt Agentur", text: "Wir betreuen jetzt 14 Solarbetriebe statt 4 — gleicher Team-Aufwand. Die Conversion Queue ist Gold wert." },
    { name: "Marcel Heinrich", role: "Founder · WärmeWelt Performance", text: "Endlich ein Tool, das Wärmepumpen-Leads genauso ernst nimmt wie PV. Unsere Hot-Lead-Quote ist 3× gestiegen." },
    { name: "Jana Friedrich", role: "Head of Ops · EnergieGruppe", text: "Der Magic Link für Solar-Kunden — krass. Onboarding eines neuen Mandanten dauert jetzt 60 Sekunden." },
  ];
  return (
    <section className="py-24 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <SectionHeader eyebrow="Testimonials" title="Stimmen aus dem Energie-Markt" sub="Was Agenturen sagen, die mit revenioo.ai mehr aus ihrer Pipeline holen." />
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {t.map((it, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="p-7 bg-white rounded-2xl border border-slate-200 shadow-lg hover:shadow-xl transition-shadow relative"
            >
              <Quote className="absolute top-5 right-5 w-7 h-7 text-orange-200" />
              <div className="flex gap-0.5 mb-4">{[1,2,3,4,5].map((s) => <Star key={s} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}</div>
              <p className="text-[15px] text-slate-700 leading-relaxed">&ldquo;{it.text}&rdquo;</p>
              <div className="mt-5 pt-5 border-t border-slate-100 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-violet-500 grid place-items-center text-white font-bold text-sm">
                  {it.name.split(" ").map((n) => n[0]).join("")}
                </div>
                <div>
                  <p className="font-bold text-slate-900 text-sm">{it.name}</p>
                  <p className="text-xs text-slate-500">{it.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============= HOW IT WORKS =============
function HowItWorks() {
  const steps = [
    { n: "01", title: "Mandant anlegen", desc: "Logo, Farbe, Subdomain — 60 Sekunden. Wholesale-Slot wird automatisch reserviert." },
    { n: "02", title: "Konfigurator einbetten", desc: "Iframe oder Snippet auf der Solar-/WP-Webseite. PV, WP oder Kombi — Sie entscheiden, was angeboten wird." },
    { n: "03", title: "Leads laufen automatisch ein", desc: "Hot-Lead-Erkennung, Preisschätzung, Pipeline-Eintrag. Ihr Mandant kontaktiert nur die priorisierten Anfragen." },
  ];
  return (
    <section className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <SectionHeader eyebrow="In 3 Schritten" title="So funktioniert es" />
        <div className="mt-14 grid md:grid-cols-3 gap-5 relative">
          {/* Verbindungslinie */}
          <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-orange-300 to-transparent" />
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              className="relative bg-white rounded-2xl border border-slate-200 p-7 shadow-md"
            >
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 grid place-items-center text-white font-extrabold text-lg shadow-lg" style={{ fontFamily: "Manrope, sans-serif" }}>
                {s.n}
              </div>
              <h3 className="mt-5 text-xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============= FAQ =============
function FAQ() {
  const items = [
    { q: "Was ist Wholesale-Pricing genau?", a: "revenioo.ai verkauft Agenturen Mandanten-Slots zu vergünstigten Konditionen (99 / 179 / 299 €). Sie verkaufen die Plattform unter Ihrem Branding zum eigenen Preis weiter und behalten die Marge. Ein eigener Stripe-Vertrag pro Mandant." },
    { q: "Funktioniert das auch für reine Wärmepumpen-Agenturen?", a: "Ja. Der Konfigurator hat einen eigenständigen WP-Pfad mit Heizungstyp, Wohnfläche, Aufstellort und einer Preisschätzung inklusive BAFA-Förderspanne." },
    { q: "Wie schnell ist ein Mandant onboarded?", a: "Stripe-Checkout → automatischer Mandant-Anlage → Magic Link in unter 60 Sekunden. Logo, Farbe und Konfigurator sind sofort konfigurierbar." },
    { q: "Was zählt als verarbeiteter Lead?", a: "Jeder Lead, der aus dem Konfigurator, manueller Anlage, Bedarfsanalyse oder KI-Chat entsteht. Sobald das monatliche Limit erreicht ist, wird ein Upgrade empfohlen — keine Sperre durch Hintertür." },
    { q: "Gibt es Volumenrabatt für viele Mandanten?", a: "Ab 5 aktiven Mandanten zeigen wir Ihnen direkt im System einen Hinweis zur Anfrage eines individuellen Agentur-Deals. Bestehende Stripe-Preise bleiben unverändert." },
    { q: "Wird das White-Label-Branding wirklich vollständig?", a: 'Logo, Primärfarbe, Akzentfarbe, Begrüßungstext, Konfigurator-Subtitle, Impressum/Datenschutz-Links — Ihr Mandant sieht "revenioo.ai" an keiner Stelle in der Kundenkommunikation.' },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-24 md:py-32 bg-white border-y border-slate-200">
      <div className="max-w-3xl mx-auto px-6 md:px-8">
        <SectionHeader eyebrow="FAQ" title="Häufige Fragen" />
        <div className="mt-12 space-y-3">
          {items.map((it, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="border border-slate-200 rounded-xl bg-white overflow-hidden">
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`faq-q-${i}`}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="font-semibold text-slate-900 text-base">{it.q}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{it.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============= FINAL CTA =============
function FinalCTA() {
  return (
    <section className="py-24 md:py-32 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-white relative overflow-hidden">
      <NoiseOverlay />
      <div className="absolute inset-0 -z-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-orange-500/30 via-transparent to-transparent blur-3xl" />
      </div>
      <div className="max-w-4xl mx-auto px-6 md:px-8 text-center relative">
        <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter" style={{ fontFamily: "Manrope, sans-serif" }}>
          Bauen Sie Ihre <span className="text-orange-400">Energie-Agentur</span> auf einer Plattform — die mitskaliert.
        </motion.h2>
        <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto">
          PV und Wärmepumpe in einer Plattform. Wholesale ab 99 €. White-Label out of the box.
          Sie skalieren — wir liefern das System.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="mt-10 flex flex-wrap justify-center gap-3">
          <Link to="/login" data-testid="final-cta-primary" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-xl font-bold text-base shadow-[0_0_60px_-10px_rgba(234,88,12,0.8)] hover:-translate-y-1 transition-all">
            Demo buchen <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/login" data-testid="final-cta-secondary" className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 text-white hover:bg-white/15 px-8 py-4 rounded-xl font-bold text-base transition-all">
            Kostenlos starten
          </Link>
        </motion.div>
        <p className="mt-6 text-xs text-slate-400">Keine Vorab-Setup-Kosten · DSGVO-konforme EU-Server · Stripe-abgesichert</p>
      </div>
    </section>
  );
}

// ============= FOOTER =============
function Footer() {
  return (
    <footer className="py-10 bg-slate-950 text-slate-400 text-sm">
      <div className="max-w-7xl mx-auto px-6 md:px-8 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-gradient-to-br from-orange-500 to-orange-600 grid place-items-center text-white"><Sun className="w-4 h-4" /></div>
          <span className="text-white font-bold">revenioo.ai</span>
          <span className="text-slate-500">· © {new Date().getFullYear()}</span>
        </div>
        <div className="flex gap-5">
          <Link to="/impressum" className="hover:text-white">Impressum</Link>
          <Link to="/datenschutz" className="hover:text-white">Datenschutz</Link>
          <Link to="/login" className="hover:text-white">Login</Link>
        </div>
      </div>
    </footer>
  );
}

// =================================================================
// ============= UI MOCKUPS (DOM, vollständig animierbar) ===========
// =================================================================

export function MockChrome({ children, label = "revenioo.ai" }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-100 bg-slate-50">
        <div className="w-2.5 h-2.5 rounded-full bg-rose-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        <span className="ml-2 text-[10px] text-slate-400 font-mono">{label}</span>
      </div>
      <div className="bg-white">{children}</div>
    </div>
  );
}

function MockAgencyDashboard({ tilt = false }) {
  const kpis = [
    { l: "Aktive Mandanten", v: "319 / 327", c: "text-violet-600" },
    { l: "Leads diesen Monat", v: "555", c: "text-slate-900" },
    { l: "Offene Angebote", v: "280", c: "text-slate-900" },
    { l: "Hot Leads", v: "221", c: "text-orange-600" },
    { l: "Gewonnener Umsatz", v: "573.800 €", c: "text-emerald-600" },
    { l: "Geretteter Umsatz", v: "734.160 €", c: "text-emerald-600" },
  ];
  return (
    <div className={tilt ? "transform-gpu hover:rotate-0 transition-transform duration-700" : ""} style={tilt ? { transform: "perspective(1500px) rotateY(-6deg) rotateX(2deg)" } : {}}>
      <MockChrome label="agentur.revenioo.ai/dashboard">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-violet-600 font-bold">Agentur-Dashboard</p>
              <h4 className="text-lg font-extrabold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Default Agency</h4>
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">Live</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {kpis.map((k, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + i * 0.06 }}
                className="p-2.5 rounded-lg bg-slate-50 border border-slate-100"
              >
                <p className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{k.l}</p>
                <p className={`text-base font-extrabold tabular-nums ${k.c}`} style={{ fontFamily: "Manrope, sans-serif" }}>{k.v}</p>
              </motion.div>
            ))}
          </div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }} className="mt-3 p-3 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Magic Link</span>
              </div>
              <span className="text-[10px] opacity-80">189 Klicks</span>
            </div>
            <p className="text-[10px] font-mono opacity-90 mt-1 truncate">solar-lead-hub.preview/portal/default-agency</p>
          </motion.div>
        </div>
      </MockChrome>
    </div>
  );
}

function MockClients() {
  const clients = [
    { name: "Andy Kay's PV-Betrieb", leads: "12/250", hot: 4, saved: "19.320 €", active: true },
    { name: "QA Tester's PV-Betrieb", leads: "12/250", hot: 4, saved: "19.320 €" },
    { name: "Ba Kay's PV-Betrieb", leads: "32/250", hot: 5, saved: "19.320 €" },
    { name: "1komma", leads: "0/250", hot: 0, saved: "0 €" },
  ];
  return (
    <MockChrome label="Mandanten">
      <div className="p-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-extrabold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Mandanten · {clients.length} aktiv</h4>
          <span className="text-[10px] text-orange-600 font-bold flex items-center gap-1">+ Neuer Mandant</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {clients.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.05 * i }}
              className={`p-2.5 rounded-lg border ${c.active ? "border-orange-300 bg-orange-50/40" : "border-slate-200 bg-white"}`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs font-bold text-slate-900 truncate">{c.name}</p>
                {c.active && <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-orange-600 text-white font-bold">AKTIV</span>}
              </div>
              <div className="grid grid-cols-3 gap-1 text-[9px]">
                <div><span className="text-slate-400">Leads</span> <span className="font-bold tabular-nums text-slate-900">{c.leads}</span></div>
                <div><span className="text-slate-400">Hot</span> <span className="font-bold tabular-nums text-orange-600">{c.hot}</span></div>
                <div><span className="text-slate-400">€</span> <span className="font-bold tabular-nums text-emerald-600">{c.saved}</span></div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </MockChrome>
  );
}

export function MockConversionQueue() {
  const rows = [
    { name: "Laura Fischer", sol: "Solaranlage", hot: true, val: "15.100 €", at: "21.05.26" },
    { name: "Markus Hoffmann", sol: "Wärmepumpe", hot: true, val: "78.720 €", at: "20.05.26" },
    { name: "Anna Schmitt", sol: "Solar + WP", hot: true, val: "15.100 €", at: "17.05.26" },
    { name: "Stefan Müller", sol: "Solaranlage", hot: false, val: "17.270 €", at: "16.05.26" },
  ];
  const icons = { "Solaranlage": Sun, "Wärmepumpe": Thermometer, "Solar + WP": Zap };
  const colors = { "Solaranlage": "bg-amber-100 text-amber-800", "Wärmepumpe": "bg-sky-100 text-sky-800", "Solar + WP": "bg-violet-100 text-violet-800" };
  return (
    <MockChrome label="Conversion Queue">
      <div className="p-3">
        <h4 className="text-sm font-extrabold text-slate-900 mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>Sofort-Leads zuerst</h4>
        <div className="space-y-1.5">
          {rows.map((r, i) => {
            const Icon = icons[r.sol];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.08 }}
                className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 border border-slate-100"
              >
                {r.hot && <Flame className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-slate-900 truncate">{r.name}</p>
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded ${colors[r.sol]} text-[8px] font-bold uppercase`}>
                      <Icon className="w-2 h-2" /> {r.sol}
                    </span>
                    {r.hot && <span className="px-1.5 py-0.5 rounded bg-orange-200 text-orange-900 text-[8px] font-bold uppercase">Sofort</span>}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold tabular-nums text-slate-900">{r.val}</p>
                  <p className="text-[9px] text-slate-400">{r.at}</p>
                </div>
                <button className="text-[9px] font-bold bg-slate-900 text-white px-2 py-1 rounded">Kontakt</button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </MockChrome>
  );
}

export function MockConfigurator() {
  return (
    <MockChrome label="Konfigurator · 3 Pfade">
      <div className="p-4">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Schritt 1 von 11</p>
        <h4 className="text-sm font-extrabold text-slate-900 mb-3" style={{ fontFamily: "Manrope, sans-serif" }}>Wofür interessieren Sie sich?</h4>
        <div className="grid grid-cols-3 gap-2">
          {[
            { Icon: Sun, label: "Solaranlage", color: "from-amber-400 to-orange-500", active: true },
            { Icon: Thermometer, label: "Wärmepumpe", color: "from-sky-500 to-blue-600" },
            { Icon: Zap, label: "Solar + WP", color: "from-violet-500 to-fuchsia-600" },
          ].map((p, i) => (
            <motion.div
              key={i}
              animate={p.active ? { y: [0, -3, 0] } : {}}
              transition={{ duration: 2, repeat: Infinity }}
              className={`p-3 rounded-lg border-2 ${p.active ? "border-orange-500 bg-orange-50/40 shadow-md" : "border-slate-200 bg-white"} text-center`}
            >
              <div className={`w-9 h-9 mx-auto rounded-lg bg-gradient-to-br ${p.color} grid place-items-center text-white mb-2`}>
                <p.Icon className="w-4 h-4" />
              </div>
              <p className="text-[10px] font-bold text-slate-900">{p.label}</p>
            </motion.div>
          ))}
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-3 p-2.5 rounded-lg bg-emerald-50 border border-emerald-200">
          <p className="text-[10px] text-emerald-800"><strong>Preisschätzung:</strong> 18.500 – 24.300 €<br/><span className="text-emerald-700">Förderung: 3.200 – 5.100 €</span></p>
        </motion.div>
      </div>
    </MockChrome>
  );
}

export function MockPipeline() {
  const cols = [
    { label: "Anfrage", count: 4, color: "border-blue-300 bg-blue-50", items: ["Christina S.", "Sabine K.", "Jürgen W.", "Petra W."] },
    { label: "Angebot", count: 2, color: "border-amber-300 bg-amber-50", items: ["Daniel K. · 28.280 €", "Laura F. · 15.100 €"] },
    { label: "Nachfass", count: 4, color: "border-orange-300 bg-orange-50", items: ["Markus H. · 78.720 €", "Thomas B. · 19.990 €"], hot: true },
    { label: "Auftrag", count: 1, color: "border-emerald-300 bg-emerald-50", items: ["Andreas L. · 15.100 €"] },
  ];
  return (
    <MockChrome label="Vertriebs-Pipeline">
      <div className="p-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-extrabold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Pipeline</h4>
          <span className="text-xs font-extrabold tabular-nums text-slate-900">189.560 €</span>
        </div>
        <div className="grid grid-cols-4 gap-1.5">
          {cols.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className={`p-1.5 rounded-lg border ${c.color}`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[9px] font-bold uppercase tracking-wider text-slate-700">{c.label}</span>
                <span className="text-[9px] font-bold text-slate-500">{c.count}</span>
              </div>
              <div className="space-y-1">
                {c.items.map((it, k) => (
                  <div key={k} className="p-1.5 rounded bg-white border border-slate-200 text-[9px] font-medium text-slate-700 flex items-center gap-1 shadow-sm">
                    {c.hot && k === 0 && <Flame className="w-2.5 h-2.5 text-orange-500" />}
                    <span className="truncate">{it}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </MockChrome>
  );
}

export function MockAIChat() {
  return (
    <MockChrome label="KI-Chat">
      <div className="p-3">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-500 to-violet-600 grid place-items-center">
            <Sparkles className="w-3.5 h-3.5 text-white" />
          </div>
          <div>
            <p className="text-xs font-bold text-slate-900">revenioo.ai · KI-Chat</p>
            <p className="text-[9px] text-emerald-600">● Online</p>
          </div>
        </div>
        <div className="space-y-2">
          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="bg-slate-100 rounded-lg rounded-tl-none p-2.5 text-[11px] text-slate-700 max-w-[80%]">
            Wie kontaktiere ich am besten einen Sofort-Lead nach 24h ohne Antwort?
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }} className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-lg rounded-tr-none p-2.5 text-[11px] ml-auto max-w-[80%] shadow-md">
            Empfehlung: WhatsApp + Foto Ihres Ansprechpartners. Vorlage:
            <p className="mt-1 italic opacity-90">&bdquo;Hallo {`{Name}`}, ich habe Ihre Anfrage zur PV-Anlage gesehen — passt morgen 14:00 für ein kurzes Update?&ldquo;</p>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }} className="flex gap-1.5">
            <span className="text-[9px] px-2 py-1 rounded-full bg-violet-100 text-violet-700 font-semibold">Reaktivierungstext</span>
            <span className="text-[9px] px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold">Einwand-Coach</span>
          </motion.div>
        </div>
      </div>
    </MockChrome>
  );
}

export function MockReporting() {
  return (
    <MockChrome label="Reporting">
      <div className="p-3">
        <h4 className="text-sm font-extrabold text-slate-900 mb-3" style={{ fontFamily: "Manrope, sans-serif" }}>Vertriebs-Kennzahlen</h4>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[
            { l: "Gewonnener Umsatz", v: "15.100 €", c: "text-emerald-600" },
            { l: "Geretteter Umsatz", v: "19.320 €", c: "text-emerald-600" },
            { l: "Reaktivierter Wert", v: "111.090 €", c: "text-orange-600" },
          ].map((k, i) => (
            <div key={i} className="p-2 rounded-lg bg-slate-50">
              <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">{k.l}</p>
              <p className={`text-sm font-extrabold tabular-nums ${k.c}`}>{k.v}</p>
            </div>
          ))}
        </div>
        <p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold mb-1.5">Leads nach Produktpfad</p>
        <div className="grid grid-cols-4 gap-1.5">
          {[
            { Icon: Sun, l: "Solar", v: "12", c: "from-amber-400 to-orange-500" },
            { Icon: Thermometer, l: "WP", v: "8", c: "from-sky-500 to-blue-600" },
            { Icon: Zap, l: "Kombi", v: "3", c: "from-violet-500 to-fuchsia-600" },
            { Icon: Flame, l: "Hot", v: "4", c: "from-rose-500 to-orange-600" },
          ].map((p, i) => (
            <div key={i} className="p-2 rounded-lg bg-white border border-slate-200 text-center">
              <div className={`w-6 h-6 mx-auto rounded-md bg-gradient-to-br ${p.c} grid place-items-center text-white mb-1`}>
                <p.Icon className="w-3 h-3" />
              </div>
              <p className="text-base font-extrabold tabular-nums text-slate-900">{p.v}</p>
              <p className="text-[8px] uppercase tracking-wider text-slate-500 font-bold">{p.l}</p>
            </div>
          ))}
        </div>
      </div>
    </MockChrome>
  );
}

export function MockProductReporting() {
  const tiles = [
    { Icon: Sun, l: "Leads · Solaranlage", v: 12, color: "from-amber-400 to-orange-500" },
    { Icon: Thermometer, l: "Leads · Wärmepumpe", v: 8, color: "from-sky-500 to-blue-600" },
    { Icon: Zap, l: "Leads · Solar + WP", v: 3, color: "from-violet-500 to-fuchsia-600" },
    { Icon: Flame, l: "Hot / Sofort-Leads", v: 4, color: "from-rose-500 to-orange-600" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {tiles.map((t, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.07 }}
          className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm relative overflow-hidden"
        >
          <div className={`absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br ${t.color} opacity-15 rounded-full blur-2xl`} />
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${t.color} grid place-items-center text-white shadow-md mb-3`}>
            <t.Icon className="w-5 h-5" />
          </div>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{t.l}</p>
          <Counter value={t.v} className="text-4xl font-extrabold tabular-nums text-slate-900 mt-1" style={{ fontFamily: "Manrope, sans-serif" }} />
        </motion.div>
      ))}
    </div>
  );
}

// =================================================================
// ============= HELPER COMPONENTS ==================================
// =================================================================

// Exports for re-use in Landing.jsx (Solar-/WP-Betriebe-Landing)
export function SectionHeader({ eyebrow, title, sub, dark = false }) {
  return (
    <div className="max-w-3xl mx-auto text-center">
      <motion.p initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className={`text-xs uppercase tracking-[0.18em] font-bold mb-3 ${dark ? "text-orange-400" : "text-orange-600"}`}>
        {eyebrow}
      </motion.p>
      <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className={`text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-[1.05] ${dark ? "text-white" : "text-slate-900"}`} style={{ fontFamily: "Manrope, sans-serif" }}>
        {title}
      </motion.h2>
      {sub && (
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className={`mt-4 text-base md:text-lg leading-relaxed ${dark ? "text-slate-300" : "text-slate-600"}`}>
          {sub}
        </motion.p>
      )}
    </div>
  );
}

export function Counter({ value, className, style }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: false });
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const controls = animate(mv, value, { duration: 0.6, ease: "easeOut" });
    return controls.stop;
  }, [value, mv]);
  useEffect(() => {
    return mv.on("change", (v) => setDisplay(Math.round(v)));
  }, [mv]);
  return <span ref={ref} className={className} style={style}>{display.toLocaleString("de-DE")}</span>;
}

export function NoiseOverlay() {
  return (
    <div
      className="absolute inset-0 opacity-[0.025] pointer-events-none"
      style={{
        backgroundImage: `url("data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' /></filter><rect width='100%' height='100%' filter='url(%23n)' /></svg>")`,
      }}
    />
  );
}

export const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};
